import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, Package } from 'lucide-react';
import ImageUpload from '@/components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('admin_token');
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const emptyProduct = { name: '', description: '', short_description: '', price: '', image_url: '', image_alt: '', category: 'Reproductive Health', in_stock: true, details: '', specifications: [], packaging_details: '', seo_title: '', seo_description: '' };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/products`).then(r => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyProduct); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, description: p.description, short_description: p.short_description || '', price: p.price || '', image_url: p.image_url || '', image_alt: p.image_alt || '', category: p.category || '', in_stock: p.in_stock !== false, details: p.details || '', specifications: p.specifications || [], packaging_details: p.packaging_details || '', seo_title: p.seo_title || '', seo_description: p.seo_description || '' }); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/products/${editing.id}`, form, authHeaders());
      } else {
        await axios.post(`${API}/admin/products`, form, authHeaders());
      }
      setDialogOpen(false); load();
    } catch (err) { alert('Error saving product'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await axios.delete(`${API}/admin/products/${id}`, authHeaders());
    load();
  };

  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-2xl font-medium">Products</h1>
        <Button className="rounded-xl" onClick={openCreate} data-testid="add-product-btn"><Plus className="w-4 h-4 mr-2" /> Add Product</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((p, i) => (
          <Card key={p.id} className="rounded-2xl border-border overflow-hidden" data-testid={`admin-product-card-${i}`}>
            <div className="aspect-[16/10] overflow-hidden bg-muted">
              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading font-medium">{p.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{p.category}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${p.in_stock ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
                  {p.in_stock ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.short_description}</p>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" className="rounded-lg flex-1" onClick={() => openEdit(p)} data-testid={`edit-product-${i}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" className="rounded-lg text-destructive hover:bg-destructive hover:text-white" onClick={() => handleDelete(p.id)} data-testid={`delete-product-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {products.length === 0 && (
        <div className="text-center py-16">
          <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No products yet. Add your first product.</p>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-sm">Name</Label><Input value={form.name} onChange={e => updateForm('name', e.target.value)} className="mt-1 rounded-xl" data-testid="product-name-input" /></div>
            <div><Label className="text-sm">Short Description</Label><Input value={form.short_description} onChange={e => updateForm('short_description', e.target.value)} className="mt-1 rounded-xl" data-testid="product-short-desc-input" /></div>
            <div><Label className="text-sm">Full Description</Label><Textarea value={form.description} onChange={e => updateForm('description', e.target.value)} rows={4} className="mt-1 rounded-xl" data-testid="product-desc-input" /></div>
            <div><Label className="text-sm">Details</Label><Textarea value={form.details} onChange={e => updateForm('details', e.target.value)} rows={3} className="mt-1 rounded-xl" data-testid="product-details-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm">Price</Label><Input value={form.price} onChange={e => updateForm('price', e.target.value)} className="mt-1 rounded-xl" data-testid="product-price-input" /></div>
              <div><Label className="text-sm">Category</Label><Input value={form.category} onChange={e => updateForm('category', e.target.value)} className="mt-1 rounded-xl" data-testid="product-category-input" /></div>
            </div>
            <ImageUpload value={form.image_url} onChange={v => updateForm('image_url', v)} label="Product Image" />
            <div>
              <Label className="text-sm">Image Alt Text (SEO)</Label>
              <Input value={form.image_alt} onChange={e => updateForm('image_alt', e.target.value)} className="mt-1 rounded-xl" placeholder={form.name ? `e.g. ${form.name} tablets — pack of 5` : 'Descriptive text for the product image'} data-testid="product-image-alt-input" />
              <p className="text-xs text-muted-foreground mt-1">Describe what is in the image. Used by Google Images and screen readers. Leave blank to use product name.</p>
            </div>
            <div><Label className="text-sm">Packaging Details</Label><Textarea value={form.packaging_details} onChange={e => updateForm('packaging_details', e.target.value)} rows={2} className="mt-1 rounded-xl" data-testid="product-packaging-input" /></div>
            {/* Specifications */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Specifications</Label>
                <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => updateForm('specifications', [...form.specifications, { key: '', value: '' }])} data-testid="add-spec-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
              </div>
              {form.specifications.map((spec, i) => (
                <div key={i} className="flex gap-2 mb-2 items-center">
                  <Input placeholder="Key (e.g. Dose)" value={spec.key} onChange={e => { const s = [...form.specifications]; s[i] = {...s[i], key: e.target.value}; updateForm('specifications', s); }} className="text-xs rounded-lg h-9" />
                  <Input placeholder="Value (e.g. 200mg)" value={spec.value} onChange={e => { const s = [...form.specifications]; s[i] = {...s[i], value: e.target.value}; updateForm('specifications', s); }} className="text-xs rounded-lg h-9" />
                  <Button variant="ghost" size="sm" className="text-destructive h-9 w-9 p-0 flex-shrink-0" onClick={() => updateForm('specifications', form.specifications.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.in_stock} onCheckedChange={v => updateForm('in_stock', v)} data-testid="product-stock-switch" />
              <Label className="text-sm">In Stock</Label>
            </div>
            {/* Product SEO */}
            <div className="p-3 bg-muted/50 rounded-xl space-y-3">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Product SEO</Label>
              <div><Label className="text-xs">SEO Title</Label><Input value={form.seo_title} onChange={e => updateForm('seo_title', e.target.value)} className="mt-1 rounded-lg text-sm" placeholder={form.name} data-testid="product-seo-title" /></div>
              <div><Label className="text-xs">SEO Description</Label><Textarea value={form.seo_description} onChange={e => updateForm('seo_description', e.target.value)} rows={2} className="mt-1 rounded-lg text-sm" placeholder="150-220 characters for search results" data-testid="product-seo-desc" /></div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full rounded-xl" data-testid="save-product-btn">
              {saving ? 'Saving...' : editing ? 'Update Product' : 'Create Product'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
